import { useState, useCallback } from 'react';
import { INSURANCE_TYPE } from '../../../utils/insuranceTypeUtils';
import { formatDateForApiStart } from '../../../utils/dateFormatter';

/**
 * 搭載文書検索の検索条件フォーム状態を管理するカスタムフック
 *
 * フォーム入力値の状態管理と、APIリクエスト用パラメータの生成を担う。
 *
 * @returns {Object} 検索条件の状態とハンドラ
 * @returns {number} returns.insuranceType - 生損保区分（INSURANCE_TYPE.LIFE | INSURANCE_TYPE.SONPO）
 * @returns {number|null} returns.selectedDocumentTypeId - 選択中の文書種別ID
 * @returns {number[]} returns.selectedCompanyIds - 選択中の保険会社IDリスト
 * @returns {string[]} returns.selectedCategories - 選択中のカテゴリリスト
 * @returns {string} returns.dateFrom - 加入時期開始（YYYY/MM/DD）
 * @returns {string} returns.dateTo - 加入時期終了（YYYY/MM/DD）
 * @returns {string} returns.keyword - 文書名キーワード
 * @returns {Function} returns.handleInsuranceTypeChange - 生損保区分変更ハンドラ（関連条件をリセット）
 * @returns {Function} returns.setSelectedDocumentTypeId - 文書種別ID設定関数
 * @returns {Function} returns.setSelectedCompanyIds - 保険会社IDs設定関数
 * @returns {Function} returns.setSelectedCategories - カテゴリリスト設定関数
 * @returns {Function} returns.setDateFrom - 加入時期開始設定関数
 * @returns {Function} returns.setDateTo - 加入時期終了設定関数
 * @returns {Function} returns.setKeyword - キーワード設定関数
 * @returns {Function} returns.handleReset - 全条件リセットハンドラ
 * @returns {Function} returns.buildSearchParams - APIリクエスト用パラメータ生成関数
 */
const useSearchCondition = () => {
  const [insuranceType, setInsuranceType] = useState(INSURANCE_TYPE.LIFE);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [keyword, setKeyword] = useState('');

  const handleInsuranceTypeChange = useCallback((type) => {
    setInsuranceType(type);
    // 文書種別・保険会社・カテゴリの選択肢は生損保区分に依存するため、切替時にリセットする
    setSelectedDocumentTypeId(null);
    setSelectedCompanyIds([]);
    setSelectedCategories([]);
  }, []);

  const handleDocumentTypeChange = useCallback((documentTypeId) => {
    setSelectedDocumentTypeId(documentTypeId);
  }, []);

  const handleReset = useCallback(() => {
    setInsuranceType(INSURANCE_TYPE.LIFE);
    setSelectedDocumentTypeId(null);
    setSelectedCompanyIds([]);
    setSelectedCategories([]);
    setDateFrom('');
    setDateTo('');
    setKeyword('');
  }, []);

  const buildSearchParams = useCallback(() => {
    // 値がある条件のみキーを追加するスパースオブジェクトとして構築し、不要なパラメータをAPIへ送らない
    const params = { insurance_type_id: insuranceType };
    if (selectedDocumentTypeId) {
      params.document_type_id = selectedDocumentTypeId;
    }
    if (selectedCompanyIds.length > 0) {
      params.insurance_company_ids = selectedCompanyIds;
    }
    if (selectedCategories.length > 0) {
      params.categories = selectedCategories;
    }
    if (dateFrom) {
      // 開始日は当日 00:00:00 に変換してAPI送信する
      params.date_from = formatDateForApiStart(dateFrom);
    }
    if (dateTo) {
      // 終了日は当日 00:00:00 に変換してAPI送信する
      params.date_to = formatDateForApiStart(dateTo);
    }
    if (keyword.trim()) {
      params.document_name_keyword = keyword.trim();
    }
    return params;
  }, [
    insuranceType,
    selectedDocumentTypeId,
    selectedCompanyIds,
    selectedCategories,
    dateFrom,
    dateTo,
    keyword,
  ]);

  return {
    insuranceType,
    selectedDocumentTypeId,
    selectedCompanyIds,
    selectedCategories,
    dateFrom,
    dateTo,
    keyword,
    handleInsuranceTypeChange,
    handleDocumentTypeChange,
    setSelectedDocumentTypeId,
    setSelectedCompanyIds,
    setSelectedCategories,
    setDateFrom,
    setDateTo,
    setKeyword,
    handleReset,
    buildSearchParams,
  };
};

export default useSearchCondition;
